import { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import { debounce } from '@mui/material/utils';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'oidc-react';
import { SearchEntry } from '@eposnow/ui-core';
import { UserContext } from '../context/UserContext';
import { apiFetch } from '../api/fetch';

interface GlobalSearchEntry {
  project: string;
  route: string;
  name: string;
  description: string;
  keywords: string[];
  userModules: string[];
  forbiddenUserModules: string[];
  userRights: string[];
  userCountries: string[];
  primaryAccountOnly: boolean;
  forbiddenUserRights: string[];
  iconKey: string;
}

const useGlobalSearch = () => {
  const { i18n } = useTranslation();
  const { appsMenu } = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;

  // Update search key when search opens
  useEffect(() => {
    setSearchKey(new Date().getTime());
  }, [globalSearchOpen]);

  const { data: rawSearchResults, isLoading } = useQuery<SearchEntry[]>({
    queryKey: ['globalSearch', searchQuery],
    queryFn: async () => {
      const entries: GlobalSearchEntry[] = await apiFetch(
        `discovery/global-search?q=${encodeURIComponent(searchQuery)}`,
        {},
        'GET',
        process.env.REACT_APP_API_V1,
        `Bearer ${maybeAccessToken}`,
        false,
        {
          'Accept-Language': i18n.language,
        }
      );

      return entries.map((entry) => {
        let project = entry?.project;
        if (entry.project === 'money') {
          if (entry.route.includes('payments')) {
            project = 'payments';
          } else {
            project = 'capital';
          }
        }

        return {
          key: entry.route,
          title: entry.name,
          project,
          description: entry.description,
          iconKey: entry.iconKey,
        };
      });
    },
    enabled: searchQuery.length >= 3,
  });

  // Search function
  const onSearch = useCallback((prompt: string) => {
    const normalizedPrompt = prompt
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

    if (normalizedPrompt.length < 3) {
      setSearchQuery('');
      return;
    }

    setSearchQuery(normalizedPrompt);
  }, []);

  // Selection handler
  const onSelect = useCallback(
    (entry: SearchEntry) => {
      let redirectBaseUrl = '';
      redirectBaseUrl = appsMenu?.[0]?.find((app) => app.projectId === entry.project)?.url || '';

      if (redirectBaseUrl) {
        // Convert to origin as money project has 'payments' and 'capital' sub-projects and the url has a path
        redirectBaseUrl = new URL(redirectBaseUrl).origin;
        setGlobalSearchOpen(false);
        setSearchQuery('');
        window.location.href = `${redirectBaseUrl}${entry.key}`;
      }
    },
    [appsMenu]
  );

  // Open/close handlers
  const openGlobalSearch = useCallback(() => {
    setSearchQuery('');
    setGlobalSearchOpen(true);
  }, []);

  const closeGlobalSearch = useCallback(() => {
    setGlobalSearchOpen(false);
    setSearchQuery('');
  }, []);

  // Keyboard shortcut handler
  const keyboardHook = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.ctrlKey &&
        e.key.toLowerCase() === 's' &&
        process.env.REACT_APP_SHOW_GLOBAL_SEARCH === 'true'
      ) {
        e.preventDefault();
        openGlobalSearch();
      }
    },
    [openGlobalSearch]
  );

  // Debounced search
  const debouncedSearch = useMemo(() => debounce(onSearch, 500), [onSearch]);

  // Cleanup debounced function
  useEffect(() => () => debouncedSearch.clear(), [debouncedSearch]);

  return {
    // State
    searchResults: rawSearchResults || [],
    loadingSearchResults: isLoading,
    globalSearchOpen,
    searchKey,

    // Actions
    openGlobalSearch,
    closeGlobalSearch,
    onSearch,
    onSelect,

    // Keyboard handling
    keyboardHook,

    // Debounced search
    debouncedSearch,
  };
};

export default useGlobalSearch;
